import type { ScopeRef } from "@enduring-systems/entitlements";
import { useState } from "react";

import type { OfferingAction, OfferingRef } from "./offering-mutation";
import { sectionKey, sectionLabel } from "./offering-sections";
import type { OfferingCard, OfferingSection } from "./offering-state";
import { OfferingActionDialog } from "./OfferingActionDialog";
import { OfferingCardView } from "./OfferingCardView";

/**
 * Which offering an action is waiting on confirmation for.
 *
 * The offering's IDENTITY, never a card. A card is a claim about one reading of
 * one scope, and the press arrives later, so storing one here would park a
 * preview that outlives the read it came from: its removal list is what the
 * store would have deleted at the time it was built, and an operator confirming
 * against it is agreeing to something that is no longer true.
 */
interface Confirming {
  readonly action: OfferingAction;
  readonly offering: OfferingRef;
}

export interface OfferingGridProps {
  /** The sections to draw, already filtered by the caller's service chip. */
  readonly sections: readonly OfferingSection[];
  /** Every section, for finding the confirmed offering whatever the filter is. */
  readonly allSections: readonly OfferingSection[];
  readonly scope: ScopeRef;
  readonly writeBlock: string | null;
  readonly busy: boolean;
  readonly onRun: (
    action: OfferingAction,
    offering: OfferingRef,
    intendedScope: ScopeRef
  ) => Promise<void>;
}

function findCard(
  sections: readonly OfferingSection[],
  offering: OfferingRef
): OfferingCard | null {
  for (const section of sections) {
    for (const card of section.offerings) {
      if (card.offering.name === offering.name && card.offering.version === offering.version) {
        return card;
      }
    }
  }
  return null;
}

/**
 * The offering cards and the confirmation over them, for ONE scope.
 *
 * ## Why this is its own component
 *
 * So the caller can key it on the scope. A confirmation belongs to the scope it
 * was opened against, and keying makes a scope change DESTROY it rather than
 * park it: React discards the state of a component whose key changed, before
 * anything renders against the new scope. Filtering the confirmation by scope
 * instead only hides it, which is enough to stop the wrong-target press but
 * leaves the snapshot sitting there, so leaving the scope and coming back
 * resurrects a dialog the operator had walked away from.
 *
 * Structural, so there is no window: not an effect (which runs after paint,
 * leaving a commit in which the stale dialog is live) and not a state write
 * during render.
 *
 * ## Why the card is looked up rather than stored
 *
 * Keying handles a scope change; it does nothing about the read moving under an
 * open dialog at the SAME scope, which a re-read does routinely. So this holds
 * only the offering's identity and finds its card in the sections it was handed
 * this render. The preview is therefore always the current reading, and a
 * confirmation whose offering has left the catalog closes itself rather than
 * describing rows nothing answers for.
 *
 * The lookup runs over `allSections`, not the filtered ones: a service chip
 * pressed while the dialog is open would otherwise close it, and the filter is a
 * view over the catalog rather than a statement about what is being confirmed.
 */
export function OfferingGrid({
  sections,
  allSections,
  scope,
  writeBlock,
  busy,
  onRun,
}: OfferingGridProps): React.ReactElement {
  const [confirming, setConfirming] = useState<Confirming | null>(null);

  const confirmingCard = confirming === null ? null : findCard(allSections, confirming.offering);

  return (
    <>
      {sections.map((section) => (
        <section
          key={sectionKey(section.namespace)}
          className="space-y-3"
          data-testid={`offering-section-${sectionKey(section.namespace)}`}
        >
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {sectionLabel(section.namespace)}
          </h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {section.offerings.map((card) => (
              <OfferingCardView
                key={`${card.offering.name}@${card.offering.version}`}
                card={card}
                writeBlock={writeBlock}
                busy={busy}
                onAct={(action) =>
                  setConfirming({
                    action,
                    offering: {
                      name: card.offering.name,
                      version: card.offering.version,
                    },
                  })
                }
              />
            ))}
          </div>
        </section>
      ))}

      {confirming !== null && confirmingCard !== null && (
        <OfferingActionDialog
          action={confirming.action}
          card={confirmingCard}
          scope={scope}
          busy={busy}
          onConfirm={() => {
            void onRun(confirming.action, confirming.offering, scope)
              /* Comes down once the write has settled, whichever way it went:
                 the outcome is reported above the grid, so a confirmation left
                 open holding an error would be a second place to read it. */
              .finally(() => setConfirming(null));
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </>
  );
}
